<template>
  <div>
    <v-form
      id="patientInfoBox"
      ref="patientInfoForm"
      v-model="isFormValid"
      @submit.prevent="onSubmit"
    >
      <h2>{{ $t("patient_info_title") }}</h2>
      <p>
        {{
          isAuthenticated
            ? $t("patient_info_disclaimer_auth")
            : $t("patient_info_disclaimer")
        }}
      </p>
      <v-row>
        <v-col cols="12" sm="6" md="6">
          <v-text-field
            v-model="initials"
            class="inputs"
            :label="$t('patient_identificator')"
            :rules="[rules.required]"
            outlined
            dense
            @input="$emit('update:name', $event)"
          ></v-text-field>
        </v-col>
        <v-col cols="12" sm="6" md="6" id="radioGender">
          <!-- Gender -->
          <v-radio-group
            v-model="genderLocal"
            :rules="[rules.required]"
            row
            @change="$emit('update:gender', $event)"
          >
            <v-radio value="Muž" :label="$t('man')" color="#415995"></v-radio>
            <v-radio
              value="Žena"
              :label="$t('woman')"
              color="#415995"
            ></v-radio>
          </v-radio-group>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12" sm="6" md="6">
          <v-menu
            ref="menu2"
            v-model="menu2"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            full-width
            max-width="290px"
            min-width="290px"
          >
            <template v-slot:activator="{ on }">
              <v-text-field
                v-model="birthDateFormatted"
                :label="$t('birth_date')"
                :rules="[rules.required, rules.date]"
                hint="DD. MM. YYYY"
                persistent-hint
                @blur="birthDate = parseDate(birthDateFormatted)"
                v-on="on"
                outlined
                dense
                clearable
                @input="$emit('update:inputBirthDate', $event)"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="birthDate"
              :allowed-dates="disableFutureDates"
              locale="cs"
              no-title
              @input="(menu2 = false), $emit('update:pickedBirthDate', $event)"
            ></v-date-picker>
          </v-menu>
        </v-col>

        <v-col cols="12" sm="6" md="6">
          <v-menu
            ref="menu1"
            v-model="menu1"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            full-width
            max-width="290px"
            min-width="290px"
          >
            <template v-slot:activator="{ on }">
              <v-text-field
                v-model="evaluationDateFormatted"
                :label="$t('result_date')"
                :rules="[rules.required, rules.date]"
                hint="DD. MM. YYYY"
                persistent-hint
                @blur="evaluationDate = parseDate(evaluationDateFormatted)"
                v-on="on"
                outlined
                dense
                clearable
                @input="$emit('update:inputDate', $event)"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="evaluationDate"
              :allowed-dates="disableFutureDates"
              locale="cs"
              no-title
              @input="(menu1 = false), $emit('update:pickedDate', $event)"
            ></v-date-picker>
          </v-menu>
        </v-col>
      </v-row>
    </v-form>
  </div>
</template>

<script>
import { smoothScrollToClass } from "@/utils/htmlUtils";
import moment from "moment";

export default {
  props: [
    "name",
    "idNumber",
    "gender",
    "inputDate",
    "pickedDate",
    "inputBirthDate",
    "pickedBirthDate",
    "validate",
    "clearForm"
  ],
  data() {
    return {
      evaluationDate: new Date().toISOString().substr(0, 10),
      birthDate: new Date().toISOString().substr(0, 10),
      menu1: false,
      menu2: false,
      evaluationDateFormatted: "",
      birthDateFormatted: "",
      genderLocal: "",
      initials: "",
      isFormValid: true,
      rules: {
        required: value => !!value || this.$t("validate_required"),
        date: v => {
          if (
            /^(0?[1-9]|[12]\d|3[01])[.]\s(0?[1-9]|1[0-2])[.]\s(19|20)\d{2}$/.test(
              v
            )
          ) {
            if (moment(v, "DD. MM. YYYY").isBefore(moment())) {
              return true;
            } else {
              return this.$t("future_date_error");
            }
          } else {
            return this.$t("wrong_date_format");
          }
        }
      }
    };
  },
  computed: {
    isAuthenticated() {
      return this.$store.getters.isUserAuthenticated;
    }
  },

  watch: {
    evaluationDate() {
      this.evaluationDateFormatted = this.formatDate(this.evaluationDate);
    },
    birthDate() {
      this.birthDateFormatted = this.formatDate(this.birthDate);
    },
    patientName: function(val) {
      this.patientName = val;
    },
    validate() {
      if (this.validate) {
        this.onSubmit();
        this.$emit("update:validate", false);
      } else {
        this.$nextTick(() => smoothScrollToClass("error--text", true));
      }
    },
    clearForm() {
      if (this.clearForm) {
        this.clearDataModel();
        this.$emit("update:clearForm", false);
      }
    }
  },

  methods: {
    formatDate(date) {
      if (!date) return null;
      const [year, month, day] = date.split("-");
      return `${day}. ${month}. ${year}`;
    },
    parseDate(date) {
      if (!date) return null;
      const [day, month, year] = date.split(". ");
      return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
    },
    onSubmit() {
      this.$refs.patientInfoForm.validate();
      if (this.isFormValid) {
        const patient = {
          gender: this.genderLocal === "Muž" ? "male" : "female",
          initials: this.initials,
          birthdate: moment(this.birthDate).format("YYYY-MM-DD"),
          evaluated_on: moment(this.evaluationDate).format("YYYY-MM-DD")
        };
        this.$emit("submit", patient);
      }
    },
    clearDataModel() {
      this.$refs.patientInfoForm.resetValidation();
      this.genderLocal = "";
      this.initials = "";
      this.birthDate = "";
      this.evaluationDate = "";
    },
    disableFutureDates(date) {
      return moment(date).isBefore(moment());
    }
  }
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">
.v-btn {
  margin-top: 2em;
  background-color: transparent !important;
  box-shadow: none;
}
#patientInfoBox {
  padding: 2em;
  box-shadow: 0px 0px 18px 0px rgba(209, 209, 209, 1);
  .v-input--selection-controls.v-input {
    margin-top: 5px;
  }
}
</style>
